/* ============================================================
 * Custom cart drawer — production-grade.
 * - Open/close, qty +/-, qty input edit, remove (with custom modal)
 * - Gift wrap toggle (infinite ON/OFF) + auto-cleanup when alone
 * - Auto-open after /cart/add.js (Dawn or YMQ — fetch + XHR)
 * - Combined cart-change + sections in single request (perf)
 * - Subtotal pulse + count pill bump on update
 * - Translated error toast
 * - Custom in-drawer confirm modal (no native confirm)
 * - Per-item shimmer loading + focus trap + return focus
 * ============================================================ */
(function() {
  'use strict';

  const ROUTES = (window.routes || {});
  const CART_ADD = (ROUTES.cart_add_url || '/cart/add') + '.js';
  const CART_CHANGE = (ROUTES.cart_change_url || '/cart/change') + '.js';
  const CART_CLEAR = (ROUTES.cart_clear_url || '/cart/clear') + '.js';
  const CART_JSON = '/cart.js';
  // Legacy gift wrap variant (kept for backward compat — never rendered in v7 UI).
  const GIFT_VARIANT_ID = 56061744808260;
  // v7 (2026-09-06): all "aux items" — gift wrap legacy + gift box normal/premium + PrioPlus+ Processing.
  // These items DON'T count as real cart items (no free shipping, no visible count),
  // and if they are the ONLY items in cart the whole cart is cleared as orphans.
  // Read from the cart-drawer data attribute so liquid stays the source of truth.
  const AUX_VARIANT_IDS = (function() {
    try {
      const drawer = document.querySelector('[data-cart-drawer]');
      const raw = drawer && drawer.getAttribute('data-aux-variant-ids');
      if (raw) return raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => n > 0);
    } catch (_) {}
    // Fallback hardcoded (must stay in sync with snippets/cart-drawer.liquid)
    return [56061744808260, 57199997714756, 57199997780292, 57200000434500];
  })();
  // v7.4 (2026-09-17): aux PRODUCT ids (source of truth = data-aux-product-ids on <cart-drawer>).
  // Any variant of these products is aux — never a jewel, never counts for free shipping / badge.
  // legacy wrap 15391313101124 · Gift box 15658750443844 · Premium box 15658750476612 · PrioPlus+ 15658750705988
  const AUX_PRODUCT_IDS = (function() {
    try {
      const drawer = document.querySelector('[data-cart-drawer]');
      const raw = drawer && drawer.getAttribute('data-aux-product-ids');
      if (raw) return raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => n > 0);
    } catch (_) {}
    // Fallback hardcoded (must stay in sync with snippets/cart-drawer.liquid)
    return [15391313101124, 15658750443844, 15658750476612, 15658750705988];
  })();
  // Accepts either a /cart.js item object ({variant_id, product_id}) or a bare variant id (number).
  const isAuxItem = (itemOrVariantId) => {
    if (itemOrVariantId && typeof itemOrVariantId === 'object') {
      const vid = parseInt(itemOrVariantId.variant_id, 10);
      const pid = parseInt(itemOrVariantId.product_id, 10);
      return AUX_VARIANT_IDS.indexOf(vid) !== -1 || AUX_PRODUCT_IDS.indexOf(pid) !== -1;
    }
    return AUX_VARIANT_IDS.indexOf(itemOrVariantId) !== -1;
  };
  // Drawer <li data-product-id> → aux? (gift box lines are rendered as normal line items)
  const isAuxLine = (li) => {
    if (!li || !li.dataset) return false;
    const pid = parseInt(li.dataset.productId, 10);
    return AUX_PRODUCT_IDS.indexOf(pid) !== -1;
  };
  // Jewel-only count from a /cart.js payload (mirrors Liquid `qualifying_count`).
  const qualifyingCount = (cart) => {
    const items = (cart && cart.items) || [];
    return items.reduce((n, it) => n + (isAuxItem(it) ? 0 : (parseInt(it.quantity, 10) || 0)), 0);
  };
  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  try { window.__cdcQualifyingCount = qualifyingCount; } catch (_) {}
  // Session-scoped preference: when user activates the gift toggle once, remember
  // it so the next /cart/add.js (e.g. PDP add after cart was emptied) auto-includes
  // the gift. Cleared when user explicitly toggles gift OFF.
  const GIFT_PREF_KEY = 'cdc_gift_preferred';

  const SEL = {
    drawer: '[data-cart-drawer]',
    inner: '.cdc__inner',
    close: '[data-close]',
    cartIcon: '#cart-icon-bubble',
    line: '[data-line-key]',
    qtyMinus: '[data-qty-minus]',
    qtyPlus: '[data-qty-plus]',
    qtyInput: '[data-qty-input]',
    remove: '[data-cart-remove]',
    giftToggle: '[data-gift-toggle]',
    modal: '[data-cart-modal]',
    modalCancel: '[data-modal-cancel]',
    modalConfirm: '[data-modal-confirm]',
    toast: '[data-cart-toast]',
    subtotal: '[data-subtotal-value]',
    countPill: '.cdc__count-pill',
  };

  let lastFocused = null;
  let pendingResolve = null;
  let toastTimer = null;
  let bumpTimer = null;
  let prevSubtotal = '';
  let prevCount = null;
  // Mark internal /cart/add.js calls with a custom header. The fetch/XHR interceptors
  // skip their post-add hook when this header is present, so internal addLine flows
  // (gift toggle, etc.) don't trigger a double applySections + open() race.
  // Header-based marking is reliable per-request — no global counter to get stuck.
  const INTERNAL_HEADER = 'X-CDC-Internal';

  // Mutation counter: tracks ongoing cart-mutating operations (add, change, clear,
  // gift cleanup). When > 0:
  //   - drawer gets `cdc-busy` class → CSS disables checkout/payment buttons
  //   - drawer gets `aria-busy="true"` → screen readers announce loading state
  // Prevents the user from clicking Checkout/Apple Pay mid-mutation (would submit
  // the OLD cart state).
  let mutationsInFlight = 0;
  function setBusy(busy) {
    if (busy) mutationsInFlight++;
    else mutationsInFlight = Math.max(0, mutationsInFlight - 1);
    const d = $drawer();
    if (!d) return;
    const isBusy = mutationsInFlight > 0;
    d.classList.toggle('cdc-busy', isBusy);
    d.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  // ---------------- DOM helpers ----------------
  const $drawer = () => document.querySelector(SEL.drawer);
  const $inner = () => document.querySelector(`${SEL.drawer} ${SEL.inner}`);
  const $modal = () => document.querySelector(`${SEL.drawer} ${SEL.modal}`);
  const $toast = () => document.querySelector(`${SEL.drawer} ${SEL.toast}`);
  const $subtotal = () => document.querySelector(`${SEL.drawer} ${SEL.subtotal}`);
  const $countPill = () => document.querySelector(`${SEL.drawer} ${SEL.countPill}`);
  const isOpen = () => { const d = $drawer(); return d && d.classList.contains('active'); };

  function getErrorText() {
    const d = $drawer();
    return (d && d.dataset.errorText) || 'Something went wrong. Please try again.';
  }

  function captureBefore() {
    const s = $subtotal();
    const p = $countPill();
    prevSubtotal = s ? s.textContent.trim() : '';
    prevCount = p ? p.textContent.trim() : null;
  }

  function flashAfter() {
    const s = $subtotal();
    if (s) {
      const cur = s.textContent.trim();
      if (cur !== prevSubtotal && prevSubtotal !== '') {
        s.classList.remove('is-flash'); void s.offsetWidth; s.classList.add('is-flash');
        setTimeout(() => s.classList.remove('is-flash'), 600);
      }
    }
    const p = $countPill();
    if (p) {
      const cur = p.textContent.trim();
      if (cur !== prevCount && prevCount !== null) {
        p.classList.remove('is-bumped'); void p.offsetWidth; p.classList.add('is-bumped');
        setTimeout(() => p.classList.remove('is-bumped'), 600);
      }
    }
  }

  // ---------------- Analytics ----------------
  // Lightweight publisher: fires Shopify Web Pixels event + DOM custom event for any
  // listener (Klaviyo, Meta CAPI, GTM, etc.). Never blocks the UI; swallows errors.
  async function fetchCartSnapshot() {
    try {
      const res = await fetch(CART_JSON, { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) { return null; }
  }

  function publishCartEvent(eventName, extraDetail) {
    // Always dispatch a DOM event synchronously so listeners can react immediately.
    try {
      document.dispatchEvent(new CustomEvent('cdc:' + eventName, { detail: extraDetail || {} }));
    } catch (_) {}
    // Shopify Web Pixels Manager (sandboxed) — push if available
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, extraDetail || {});
      }
    } catch (_) {}
    // Klaviyo — fire common cart events if klaviyo global is present
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function' && extraDetail && extraDetail.cart) {
        const c = extraDetail.cart;
        const klEvent = eventName === 'cart_viewed' ? 'Viewed Cart' : 'Updated Cart';
        window.klaviyo.push(['track', klEvent, {
          $value: (c.total_price || 0) / 100,
          ItemCount: c.item_count || 0,
          Items: (c.items || []).map(i => ({
            ProductID: i.product_id, VariantID: i.variant_id,
            ProductName: i.product_title, Quantity: i.quantity,
            ItemPrice: (i.final_price || 0) / 100,
            RowTotal: (i.final_line_price || 0) / 100,
          })),
        }]);
      }
    } catch (_) {}
  }

  let lastEmittedSig = '';
  async function emitCartUpdated(reason) {
    const cart = await fetchCartSnapshot();
    if (!cart) return;
    const sig = (cart.token || '') + ':' + cart.item_count + ':' + cart.total_price;
    if (sig === lastEmittedSig && reason !== 'force') return;
    lastEmittedSig = sig;
    publishCartEvent('cart_updated', { cart, reason: reason || 'change' });
  }

  async function emitCartViewed() {
    const cart = await fetchCartSnapshot();
    if (!cart) return;
    publishCartEvent('cart_viewed', { cart });
  }

  // ---------------- Open / close ----------------
  function open(triggeredBy) {
    const d = $drawer(); if (!d) return;
    const wasAlreadyOpen = isOpen();
    if (triggeredBy && triggeredBy.focus) lastFocused = triggeredBy;
    else if (!lastFocused) lastFocused = document.activeElement;
    d.classList.add('active');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cdc-open');
    setTimeout(() => { const i = $inner(); if (i) i.focus(); }, 50);
    if (!wasAlreadyOpen) emitCartViewed();
    initRecommendations();
  }

  function close() {
    const d = $drawer(); if (!d) return;
    d.classList.remove('active');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cdc-open');
    if (lastFocused && lastFocused.focus && lastFocused.isConnected) {
      try { lastFocused.focus(); } catch (e) {}
    }
    lastFocused = null;
  }

  function setLineBusy(li, busy) { if (li) li.classList.toggle('is-busy', !!busy); }

  // ============================================================
  // Optimistic price/subtotal update for qty +/- changes.
  // Reads raw cents from data attributes set by Liquid, computes the new line total
  // and subtotal, and replaces just the digits in the existing money strings —
  // preserving locale format (€31,00 vs $31.00 vs Kč 750 etc).
  // applySections will overwrite with authoritative server values when the change.js
  // response arrives, but the user sees the new numbers INSTANTLY (no 1-3s wait).
  // ============================================================
  function formatMoneyLikeExisting(existingText, newCents) {
    if (typeof existingText !== 'string') return null;
    const text = existingText.trim();
    // Find the longest digit-with-separator span (the actual money value)
    const m = text.match(/[\d][\d.,]*/);
    if (!m) return null;
    const oldNum = m[0];

    // Detect decimal vs thousands separator
    const hasComma = oldNum.includes(',');
    const hasDot = oldNum.includes('.');
    let decSep = '', thouSep = '';
    if (hasComma && hasDot) {
      decSep = oldNum.lastIndexOf(',') > oldNum.lastIndexOf('.') ? ',' : '.';
      thouSep = decSep === ',' ? '.' : ',';
    } else if (hasComma) {
      const tail = oldNum.split(',').pop();
      decSep = tail.length === 2 ? ',' : '';
      thouSep = decSep ? '' : ',';
    } else if (hasDot) {
      const tail = oldNum.split('.').pop();
      decSep = tail.length === 2 ? '.' : '';
      thouSep = decSep ? '' : '.';
    }

    // Determine if currency uses cents (most do; JPY/KRW/etc don't)
    const usesCents = decSep !== '';
    const absCents = Math.abs(newCents);
    const intPart = usesCents ? Math.floor(absCents / 100) : Math.floor(absCents);
    const fracPart = usesCents ? String(absCents % 100).padStart(2, '0') : '';

    let intStr = String(intPart);
    if (thouSep && intStr.length > 3) {
      intStr = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    }
    const sign = newCents < 0 ? '-' : '';
    const newNum = sign + intStr + (decSep ? decSep + fracPart : '');
    return text.replace(oldNum, newNum);
  }

  // Atomic helper: shift the displayed subtotal by `deltaCents` and pulse.
  // Reads cumulative cents from data-subtotal-cents (set by Liquid) so multiple rapid
  // calls compose correctly. applySections will overwrite with server-authoritative
  // value when the response arrives.
  function applySubtotalDelta(deltaCents) {
    if (!deltaCents) return;
    const subEl = $subtotal();
    if (!subEl) return;
    const oldSub = parseInt(subEl.dataset.subtotalCents || '0', 10);
    const newSub = Math.max(0, oldSub + deltaCents);
    const newText = formatMoneyLikeExisting(subEl.textContent, newSub);
    if (!newText) return;
    subEl.textContent = newText;
    subEl.dataset.subtotalCents = String(newSub);
    subEl.classList.remove('is-flash'); void subEl.offsetWidth; subEl.classList.add('is-flash');
    setTimeout(() => subEl.classList.remove('is-flash'), 600);
  }

  // Atomic helper: shift count badges by `delta` items.
  // Updates BOTH:
  //   - .cdc__count-pill inside the drawer header
  //   - .cart-count-bubble in the SITE header (top-right cart icon)
  // The page-header bubble is critical: without this, it lags 1-3s behind the
  // drawer (only refreshed by applySections via the cart-icon-bubble section).
  function applyCountDelta(delta) {
    if (!delta) return;
    // 1) Drawer count pill
    const pill = $countPill();
    if (pill) {
      const cur = parseInt((pill.textContent || '').trim(), 10) || 0;
      const newCount = Math.max(0, cur + delta);
      if (newCount === 0) {
        pill.style.display = 'none';
      } else {
        pill.textContent = String(newCount);
        pill.style.removeProperty('display');
        pill.classList.remove('is-bumped'); void pill.offsetWidth; pill.classList.add('is-bumped');
        setTimeout(() => pill.classList.remove('is-bumped'), 600);
      }
    }
    // 2) Page-header cart bubble(s) — there can be multiple (desktop + mobile nav)
    document.querySelectorAll('.cart-count-bubble').forEach(el => {
      const span = el.querySelector('span[aria-hidden="true"]');
      if (!span) return;
      const cur = parseInt((span.textContent || '').trim(), 10) || 0;
      const newCount = Math.max(0, cur + delta);
      if (newCount === 0) {
        el.style.display = 'none';
      } else if (newCount < 100) {
        el.style.removeProperty('display');
        span.textContent = String(newCount);
      }
    });
  }

  // Force-set count badges (use when we know the absolute target, e.g. on full empty)
  function setCartCount(count) {
    const pill = $countPill();
    if (pill) {
      if (count === 0) {
        pill.style.display = 'none';
      } else {
        pill.textContent = String(count);
        pill.style.removeProperty('display');
      }
    }
    document.querySelectorAll('.cart-count-bubble').forEach(el => {
      if (count === 0) {
        el.style.display = 'none';
      } else {
        const span = el.querySelector('span[aria-hidden="true"]');
        if (span && count < 100) span.textContent = String(count);
        el.style.removeProperty('display');
      }
    });
  }

  function optimisticUpdateLineAndSubtotal(li, oldQty, newQty) {
    if (!li) return;
    const unitPrice = parseInt(li.dataset.lineUnitPrice || '0', 10);
    const unitOriginal = parseInt(li.dataset.lineOriginalUnitPrice || '0', 10);
    if (!unitPrice || oldQty === newQty) return;
    const qtyDelta = newQty - oldQty;

    // 1) Line price (final)
    const lineFinalEl = li.querySelector('[data-line-final-cents]');
    if (lineFinalEl) {
      const newLineCents = unitPrice * newQty;
      const newText = formatMoneyLikeExisting(lineFinalEl.textContent, newLineCents);
      if (newText) {
        lineFinalEl.textContent = newText;
        lineFinalEl.dataset.lineFinalCents = String(newLineCents);
      }
    }

    // 2) Line original (strike-through, when on sale)
    const lineOrigEl = li.querySelector('[data-line-original-cents]');
    if (lineOrigEl && unitOriginal) {
      const newOrigCents = unitOriginal * newQty;
      const newText = formatMoneyLikeExisting(lineOrigEl.textContent, newOrigCents);
      if (newText) {
        lineOrigEl.textContent = newText;
        lineOrigEl.dataset.lineOriginalCents = String(newOrigCents);
      }
    }

    // 3) Subtotal + count pill
    applySubtotalDelta(unitPrice * qtyDelta);
    // v7.4: aux lines (gift box) never move the jewel-only badge (Liquid re-render is authoritative)
    if (!isAuxLine(li)) applyCountDelta(qtyDelta);
  }

  // ---------------- Optimistic remove (instant DOM collapse, fetch in background) ----------------
  function collapseAndRemove(li, isLast) {
    if (!li) return;
    // Lock current height for smooth collapse
    const h = li.offsetHeight;
    li.style.height = h + 'px';
    li.style.overflow = 'hidden';
    // Force layout flush, then animate to 0
    void li.offsetHeight;
    requestAnimationFrame(() => {
      li.classList.add('is-removing');
      li.style.height = '0';
      li.style.paddingTop = '0';
      li.style.paddingBottom = '0';
      li.style.marginTop = '0';
      li.style.marginBottom = '0';
      li.style.borderBottomColor = 'transparent';
    });
    // Remove from DOM after animation
    setTimeout(() => {
      if (!li.isConnected) return;
      li.remove();
      // If this was the last item, show optimistic empty placeholder while server confirms
      if (isLast) showOptimisticEmpty();
    }, 200);
  }

  function showOptimisticEmpty() {
    const inner = $inner();
    if (!inner) return;
    // v5.1 2026-08-19: add is-empty class to drawer wrap IMMEDIATELY so defensive CSS
    // (.cart-drawer-custom.is-empty .cdc__shipping-bar { display: none }) kicks in
    // even if applySections is slow/fails. Guards against race condition where the
    // inline display:none we set below is wiped by applySections replacing innerHTML
    // BUT the server-rendered response still contains a stale shipping bar.
    const drawerWrap = $drawer();
    if (drawerWrap) drawerWrap.classList.add('is-empty');
    // Hide filled-state elements (non-destructive — applySections will replace cleanly later)
    inner.querySelectorAll('.cdc__form-wrap, .cdc__form, .cdc__upsell, .cdc__footer, .cdc__shipping-bar').forEach(el => {
      el.style.display = 'none';
    });
    // Remove any prior optimistic empty/placeholder
    inner.querySelectorAll('.cdc__optimistic-empty, .cdc__empty--optimistic').forEach(el => el.remove());

    // Render the FULL empty state (with reco cards) instantly by cloning the
    // Liquid-rendered <template>. Avoids the loader-dots wait while clearCart's
    // round-trip completes. clearCart's applySections will later replace innerHTML
    // with the server-rendered empty state — same content, no flicker.
    const template = inner.querySelector('[data-cdc-empty-template]');
    if (template && template.content) {
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'cdc__empty cdc__empty--optimistic';
      emptyDiv.appendChild(template.content.cloneNode(true));
      const modal = inner.querySelector(SEL.modal);
      if (modal) modal.parentNode.insertBefore(emptyDiv, modal);
      else inner.appendChild(emptyDiv);
    } else {
      // Fallback (template missing for some reason): minimal loader dots
      const placeholder = document.createElement('div');
      placeholder.className = 'cdc__optimistic-empty';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = '<span><span class="cdc__loader-dot"></span><span class="cdc__loader-dot"></span><span class="cdc__loader-dot"></span></span>';
      const modal = inner.querySelector(SEL.modal);
      if (modal) modal.parentNode.insertBefore(placeholder, modal);
      else inner.appendChild(placeholder);
    }

    // Force ALL cart counters to 0 immediately (drawer pill + site-header bubble).
    // applySections will later re-render the cart-icon-bubble section to confirm.
    setCartCount(0);
  }

  // ---------------- Toast ----------------
  function showToast(text) {
    const t = $toast(); if (!t) return;
    t.textContent = text;
    t.removeAttribute('hidden');
    requestAnimationFrame(() => t.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      t.classList.remove('is-visible');
      setTimeout(() => t.setAttribute('hidden', ''), 300);
    }, 3500);
  }
  function showError() { showToast(getErrorText()); }

  // ---------------- Modal (in-drawer confirm) ----------------
  // Honors a session-scoped "don't ask again" preference (set when user ticks the
  // checkbox + clicks Confirm). Stored in sessionStorage so it resets on tab close.
  const SKIP_CONFIRM_KEY = 'cdc_skip_remove_confirm';
  function confirmModal(text) {
    try {
      if (sessionStorage.getItem(SKIP_CONFIRM_KEY) === '1') {
        return Promise.resolve(true);
      }
    } catch (_) {}
    // Cancel any prior pending modal so the new one isn't blocked by stale state
    if (pendingResolve) { try { pendingResolve(false); } catch (_) {} }
    return new Promise(resolve => {
      const m = $modal();
      if (!m) { resolve(window.confirm(text)); return; }
      m.querySelector('.cdc__modal-text').textContent = text;
      // Reset the skip-future checkbox each open
      const skipChk = m.querySelector('[data-modal-skip-future]');
      if (skipChk) skipChk.checked = false;
      m.removeAttribute('hidden');
      void m.offsetWidth;
      requestAnimationFrame(() => requestAnimationFrame(() => m.classList.add('is-visible')));
      setTimeout(() => {
        const cancel = m.querySelector(SEL.modalCancel);
        if (cancel) cancel.focus();
      }, 60);
      pendingResolve = (result) => {
        // Persist "don't ask again" only if user CONFIRMED with checkbox ticked
        if (result && skipChk && skipChk.checked) {
          try { sessionStorage.setItem(SKIP_CONFIRM_KEY, '1'); } catch (_) {}
        }
        m.classList.remove('is-visible');
        setTimeout(() => m.setAttribute('hidden', ''), 220);
        pendingResolve = null;
        resolve(result);
      };
    });
  }

  // ---------------- Apply sections from a JSON response ----------------
  function applySections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections['cart-drawer'];
      const newDrawer = tmp.querySelector(SEL.drawer);
      const oldDrawer = $drawer();
      if (newDrawer && oldDrawer) {
        const wasOpen = isOpen();
        oldDrawer.innerHTML = newDrawer.innerHTML;
        oldDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        // Sync data-error-text in case server changed it
        const newErr = newDrawer.dataset.errorText;
        if (newErr) oldDrawer.dataset.errorText = newErr;
        if (wasOpen) {
          oldDrawer.classList.add('active');
          document.body.classList.add('overflow-hidden');
          document.body.classList.add('cdc-open');
        }
      }
    }
    if (sections['cart-icon-bubble']) {
      document.querySelectorAll('#cart-icon-bubble').forEach(el => {
        const tmp = document.createElement('div');
        tmp.innerHTML = sections['cart-icon-bubble'];
        const inner = tmp.querySelector('#cart-icon-bubble');
        el.innerHTML = inner ? inner.innerHTML : tmp.innerHTML;
      });
    }
    initRecommendations();
  }

  // Fallback: refetch sections via GET (used by fetch-interceptor and gift cleanup)
  async function refetchSections() {
    try {
      const url = (window.location.pathname || '/') + '?sections=' + SECTIONS + '&_t=' + Date.now();
      // v7.4 (2026-09-17): NOT 'application/json' — on /products/<handle> URLs Shopify would answer
      // with the product JSON ({product:{…}}) instead of the requested sections, so the post-add
      // hook silently did nothing on PDPs. '*/*' returns the sections JSON on every page type.
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Accept': '*/*' },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      applySections(data);
    } catch (err) { console.error('[cart-drawer] refetch failed', err); }
  }

  // Bump cart icon (visual reinforcement after add)
  function bumpCartIcon() {
    const icon = document.querySelector(SEL.cartIcon);
    if (!icon) return;
    icon.classList.remove('cdc-bumped'); void icon.offsetWidth; icon.classList.add('cdc-bumped');
    clearTimeout(bumpTimer);
    bumpTimer = setTimeout(() => icon.classList.remove('cdc-bumped'), 600);
  }

  // ---------------- Aux cleanup (v7: cart has ONLY aux items → remove them all) ----------------
  // Aux items = gift wrap legacy + gift box normal/premium + PrioPlus+ Processing.
  // If the cart contains ONLY aux items (no real products), remove them all in a single
  // updates call so the user lands on the empty state. Previously only the legacy gift
  // wrap variant was cleaned up (ensureGiftAlone).
  async function ensureGiftAlone() {
    try {
      const res = await fetch(CART_JSON, { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) return false;
      const cart = await res.json();
      const items = cart.items || [];
      if (items.length === 0) return false;
      const auxItems = items.filter(i => isAuxItem(i));
      const realCount = items.length - auxItems.length;
      if (auxItems.length === 0 || realCount > 0) return false;

      // Only aux items left → clear each one to quantity 0 via a single /cart/update.js call.
      const updates = {};
      auxItems.forEach(it => { updates[it.key] = 0; });
      const r = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates, sections: SECTIONS, sections_url: window.location.pathname }),
      });
      if (!r.ok) {
        console.error('[ensureGiftAlone/v7] failed to remove orphan aux items', { status: r.status, count: auxItems.length });
        return false;
      }
      const data = await r.json();
      applySections(data.sections);
      return true;
    } catch (err) {
      console.error('[ensureGiftAlone/v7] error', err);
      return false;
    }
  }

  // ---------------- Cart actions (single round-trip with sections) ----------------
  async function changeLine(payload, opts) {
    // /cart/change.js is NOT intercepted by the post-add hook (only /cart/add.js is),
    // so no internal-marking needed here.
    const scope = (opts && opts.scope) || null;
    const skipCleanup = (opts && opts.skipCleanup) === true;
    setLineBusy(scope, true);
    setBusy(true);
    captureBefore();
    try {
      const body = Object.assign({}, payload, { sections: SECTIONS, sections_url: window.location.pathname });
      const res = await fetch(CART_CHANGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { showError(); return; }
      const data = await res.json();
      applySections(data.sections);
      flashAfter();
      if (!skipCleanup) {
        const cleaned = await ensureGiftAlone();
        if (cleaned) flashAfter();
      }
      emitCartUpdated('change');
    } catch (err) {
      showError();
    } finally {
      setLineBusy(scope, false);
      setBusy(false);
    }
  }

  // Robust full-cart clear with TWO-LEVEL fallback. Used for "remove last item" so we
  // get an atomic empty (incl. orphan gift wrap) without the legacy 2-step dance.
  // Strategy:
  //   1. Try /cart/clear.js with NO body (max compatibility — some stores 422 on JSON body)
  //      → if OK, refetchSections() to update UI (clear.js doesn't always return sections)
  //   2. Fallback: change.js on the line + ensureGiftAlone (legacy proven path)
  //   3. Only show error toast if BOTH attempts fail
  async function clearCart(fallbackLineKey) {
    captureBefore();
    setBusy(true);
    try {
      // Attempt 1: atomic /cart/clear.js
      try {
        const res = await fetch(CART_CLEAR, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            [INTERNAL_HEADER]: '1',
          },
        });
        if (res.ok) {
          // clear.js usually returns the empty cart but may not include sections —
          // refresh UI explicitly to guarantee correct render.
          await refetchSections();
          // Defensive: force counters to 0 in case section render returned stale.
          // Without this, the page-header cart-count-bubble can lag (Shopify edge cache).
          setCartCount(0);
          flashAfter();
          emitCartUpdated('clear');
          return true;
        }
        console.warn('[clearCart] /cart/clear.js returned', res.status, '— trying fallback');
      } catch (err) {
        console.warn('[clearCart] /cart/clear.js threw, trying fallback', err);
      }

      // Attempt 2 (fallback): legacy per-line change + gift cleanup
      if (fallbackLineKey) {
        try {
          const res = await fetch(CART_CHANGE, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              [INTERNAL_HEADER]: '1',
            },
            body: JSON.stringify({ id: fallbackLineKey, quantity: 0 }),
          });
          if (res.ok) {
            await ensureGiftAlone();
            await refetchSections();
            setCartCount(0);
            flashAfter();
            emitCartUpdated('clear');
            return true;
          }
          console.error('[clearCart] fallback /cart/change.js returned', res.status);
        } catch (err) {
          console.error('[clearCart] fallback failed', err);
        }
      }

      // Both attempts failed — surface the error
      showError();
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addLine(payload) {
    captureBefore();
    setBusy(true);
    try {
      const body = Object.assign({}, payload, { sections: SECTIONS, sections_url: window.location.pathname });
      const res = await fetch(CART_ADD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // Mark internal — interceptor skips post-add hook for these (we apply sections ourselves below)
          [INTERNAL_HEADER]: '1',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = getErrorText();
        let errBody = null;
        try {
          errBody = await res.json();
          if (errBody && errBody.description) msg = errBody.description;
          else if (errBody && errBody.message) msg = errBody.message;
        } catch (_) {}
        // Detailed diagnostics — visible in DevTools to identify Shopify-side rejection causes
        console.error('[addLine] Shopify rejected /cart/add.js', {
          status: res.status,
          statusText: res.statusText,
          payload: payload,
          serverResponse: errBody,
          shownMessage: msg,
        });
        showToast(msg);
        return false;
      }
      const data = await res.json();
      applySections(data.sections);
      flashAfter();
      emitCartUpdated('add');
      return true;
    } catch (err) {
      console.error('[addLine] network/parse error', err, 'payload:', payload);
      showError();
      return false;
    } finally {
      setBusy(false);
    }
  }

  // ---------------- Event delegation ----------------
  document.addEventListener('click', async function(e) {
    if (e.target.closest(SEL.modalCancel)) { e.preventDefault(); if (pendingResolve) pendingResolve(false); return; }
    if (e.target.closest(SEL.modalConfirm)) { e.preventDefault(); if (pendingResolve) pendingResolve(true); return; }

    // (Continue Shopping CTA now uses standard data-close attribute — handled below by closeEl)

    const closeEl = e.target.closest(SEL.close);
    if (closeEl) {
      e.preventDefault(); close(); return;
    }

    const cartIconLink = e.target.closest(SEL.cartIcon);
    if (cartIconLink) {
      // Allow new-tab/window: cmd/ctrl/shift click + middle click pass through to the link
      if (e.metaKey || e.ctrlKey || e.shiftKey || (typeof e.button === 'number' && e.button > 0)) return;
      e.preventDefault(); open(cartIconLink); return;
    }

    const removeBtn = e.target.closest(SEL.remove);
    if (removeBtn) {
      e.preventDefault();
      const li = removeBtn.closest(SEL.line); if (!li) return;
      const ok = await confirmModal(removeBtn.dataset.confirmText || 'Remove this item?');
      if (!ok) return;
      const allItems = document.querySelectorAll('.cdc__items > .cdc__item');
      const isLastItem = allItems.length === 1;
      const lineKey = li.dataset.lineKey;

      // Optimistic: subtract this line's price from subtotal + qty from count pill
      // (instant visual feedback, no 1-3s wait for the fetch)
      const lineFinalEl = li.querySelector('[data-line-final-cents]');
      const linePriceCents = lineFinalEl ? parseInt(lineFinalEl.dataset.lineFinalCents || '0', 10) : 0;
      const qtyInput = li.querySelector(SEL.qtyInput);
      const lineQty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      applySubtotalDelta(-linePriceCents);
      // v7.4: removing a gift box must not decrement the jewel-only badge
      if (!isAuxLine(li)) applyCountDelta(-lineQty);

      // Collapse animation in parallel
      collapseAndRemove(li, isLastItem);

      if (isLastItem) {
        // Atomic empty with fallback. Pass lineKey for the fallback path.
        clearCart(lineKey);
      } else {
        changeLine({ id: lineKey, quantity: 0 }, { skipCleanup: true });
      }
      return;
    }

    const minus = e.target.closest(SEL.qtyMinus);
    if (minus) {
      e.preventDefault();
      if (minus.disabled) return;
      const li = minus.closest(SEL.line); if (!li) return;
      if (li.classList.contains('is-qty-pending')) return;
      const input = li.querySelector(SEL.qtyInput); if (!input) return;
      const cur = parseInt(input.value, 10) || 1;
      if (cur <= 1) return;
      const newQty = cur - 1;
      input.value = newQty;
      if (newQty <= 1) minus.disabled = true;
      // Optimistic visual update — no scope passed → no shimmer overlay → user sees new qty INSTANTLY
      optimisticUpdateLineAndSubtotal(li, cur, newQty);
      li.classList.add('is-qty-pending');
      try {
        await changeLine({ id: li.dataset.lineKey, quantity: newQty }, { skipCleanup: true });
      } finally {
        if (li.isConnected) li.classList.remove('is-qty-pending');
      }
      return;
    }

    const plus = e.target.closest(SEL.qtyPlus);
    if (plus) {
      e.preventDefault();
      const li = plus.closest(SEL.line); if (!li) return;
      if (li.classList.contains('is-qty-pending')) return;
      const input = li.querySelector(SEL.qtyInput); if (!input) return;
      const cur = parseInt(input.value, 10) || 1;
      const newQty = cur + 1;
      input.value = newQty;
      // Re-enable minus if it was disabled
      const minusBtn = li.querySelector(SEL.qtyMinus);
      if (minusBtn) minusBtn.disabled = false;
      // Optimistic visual update — no scope passed → no shimmer overlay → user sees new qty INSTANTLY
      optimisticUpdateLineAndSubtotal(li, cur, newQty);
      li.classList.add('is-qty-pending');
      try {
        await changeLine({ id: li.dataset.lineKey, quantity: newQty }, { skipCleanup: true });
      } finally {
        if (li.isConnected) li.classList.remove('is-qty-pending');
      }
      return;
    }

    // v7.1 (2026-09-06): pulsante "+" per gift box add-to-cart — semplice addLine +1 qty
    const addLineBtn = e.target.closest('[data-add-line]');
    if (addLineBtn) {
      e.preventDefault();
      if (addLineBtn.classList.contains('is-loading')) return;
      const variantId = parseInt(addLineBtn.dataset.variantId, 10);
      if (!variantId) return;
      const priceCents = parseInt(addLineBtn.dataset.priceCents || '0', 10);
      addLineBtn.classList.add('is-loading');
      // Optimistic subtotal update: aggiunge il price al totale prima del round-trip
      if (priceCents) applySubtotalDelta(priceCents);
      try {
        const ok = await addLine({ items: [{ id: variantId, quantity: 1 }] });
        if (!ok) {
          // addLine ha già mostrato toast con errore server — rollback delta
          if (priceCents) applySubtotalDelta(-priceCents);
          console.error('[add-line] addLine returned false — rolling back optimistic delta');
          return;
        }
        flashAfter();
        emitCartUpdated('add_line_upsell');
      } catch (err) {
        if (priceCents) applySubtotalDelta(-priceCents);
        console.error('[add-line] uncaught error', err);
        try { showError(); } catch (_) {}
      } finally {
        // Re-select post applySections (DOM potrebbe essere stato ri-renderizzato)
        const t = document.querySelector('[data-add-line][data-variant-id="' + variantId + '"]');
        if (t) t.classList.remove('is-loading');
      }
      return;
    }

    const giftBtn = e.target.closest(SEL.giftToggle);
    if (giftBtn) {
      e.preventDefault();
      if (giftBtn.classList.contains('is-loading')) return;
      const variantId = parseInt(giftBtn.dataset.giftVariantId, 10) || GIFT_VARIANT_ID;
      const wasOn = giftBtn.classList.contains('is-on');
      const userWantsIn = !wasOn;

      // Optimistic UI flip
      giftBtn.classList.add('is-loading');
      giftBtn.classList.toggle('is-on', userWantsIn);
      giftBtn.setAttribute('aria-checked', String(userWantsIn));

      // Optimistic subtotal update only (v6.2 — Lin feedback 2026-06-02).
      // Per nuova UX: gift wrap NON conta come articolo nel badge header (è un upsell, non un articolo).
      // Update SOLO il totale nel CTA; NO change al count pill o cart-count-bubble.
      const giftPriceCents = parseInt(giftBtn.dataset.giftPriceCents || '0', 10);
      const subtotalDelta = userWantsIn ? giftPriceCents : -giftPriceCents;
      if (subtotalDelta) applySubtotalDelta(subtotalDelta);

      // Persist user preference in sessionStorage:
      //   - Toggle ON → save '1' so future PDP adds auto-include the gift
      //   - Toggle OFF → clear (user explicitly opted out)
      try {
        if (userWantsIn) sessionStorage.setItem(GIFT_PREF_KEY, '1');
        else sessionStorage.removeItem(GIFT_PREF_KEY);
      } catch (_) {}

      // Helper: rollback the visual state on the (possibly NEW after applySections) toggle
      // — also reverts the optimistic deltas so the user doesn't see wrong totals on failure.
      const rollback = () => {
        const t = document.querySelector(SEL.giftToggle);
        if (t) {
          t.classList.toggle('is-on', wasOn);
          t.setAttribute('aria-checked', String(wasOn));
          t.classList.remove('is-loading');
        }
        if (subtotalDelta) applySubtotalDelta(-subtotalDelta);
        // v6.2: count pill non viene toccato per gift wrap toggle (vedi above)
        // Restore previous pref state on failure
        try {
          if (wasOn) sessionStorage.setItem(GIFT_PREF_KEY, '1');
          else sessionStorage.removeItem(GIFT_PREF_KEY);
        } catch (_) {}
      };

      try {
        // ============================================================
        // SIMPLIFIED FLOW (rewritten 2026-04-28):
        // 1) Read authoritative cart via /cart.js (with one retry on transient failure)
        // 2) Decide action based on (userWantsIn vs isInCartNow):
        //    - WANT IN, NOT IN → addLine()
        //    - WANT OUT, IS IN → changeLine(qty:0)
        //    - already in sync → just refetchSections() to bring DOM up to date
        // 3) Trust addLine/changeLine to applySections from POST response (authoritative,
        //    never cached). Do NOT do a follow-up refetchSections() — that GET could
        //    return cached/stale section render and overwrite the correct state.
        // 4) If addLine returns false, IT already showed a toast with the specific
        //    server error → just rollback, don't show another generic toast.
        // ============================================================
        let cart = await fetchCartSnapshot();
        if (!cart) {
          await new Promise(r => setTimeout(r, 250));
          cart = await fetchCartSnapshot();
        }
        if (!cart) {
          console.error('[gift-toggle] /cart.js unreachable after retry');
          throw new Error('cart fetch failed');
        }

        const giftLine = (cart.items || []).find(i => i.variant_id === variantId);
        const isInCartNow = !!giftLine;

        console.debug('[gift-toggle]', { wasOn, userWantsIn, isInCartNow, variantId, lineKey: giftLine && giftLine.key });

        if (userWantsIn && !isInCartNow) {
          const ok = await addLine({ items: [{ id: variantId, quantity: 1 }] });
          if (!ok) {
            // addLine already showed a toast with the specific error from Shopify
            // (logged in console). Just rollback — DO NOT throw (would show 2nd toast).
            console.error('[gift-toggle] addLine returned false — rolling back');
            rollback();
            return;
          }
        } else if (!userWantsIn && isInCartNow) {
          await changeLine({ id: giftLine.key, quantity: 0 }, { skipCleanup: true });
        } else {
          // Already in sync (user clicked but state already matches). Possible reasons:
          //   - rapid double-click before applySections finished
          //   - state desync between DOM and server (e.g., another tab modified cart)
          // Force a section refetch to bring DOM into agreement with server.
          console.debug('[gift-toggle] state already in sync, refetching for UI consistency');
          await refetchSections();
        }

        flashAfter();
        emitCartUpdated('gift_toggle');
      } catch (err) {
        console.error('[gift-toggle] uncaught error', err);
        rollback();
        try { showError(); } catch (_) {}
      } finally {
        // Clear loading state on the post-render toggle (giftBtn closure may be detached)
        const t = document.querySelector(SEL.giftToggle);
        if (t) t.classList.remove('is-loading');
      }
      return;
    }
  });

  // Direct qty input edit (debounced, validated min=1)
  let qtyTimer = null;
  document.addEventListener('change', function(e) {
    const input = e.target.closest(SEL.qtyInput); if (!input) return;
    const li = input.closest(SEL.line); if (!li) return;
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(async () => {
      let qty = parseInt(input.value, 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      input.value = qty;
      await changeLine({ id: li.dataset.lineKey, quantity: qty }, { scope: li, skipCleanup: true });
    }, 300);
  });

  // ---------------- Keyboard ----------------
  document.addEventListener('keydown', function(e) {
    // Bug fix: pressing Enter in qty input would submit the form → /checkout
    // Instead, blur the input which triggers the change handler (qty update)
    if (e.key === 'Enter' && e.target.closest && e.target.closest(SEL.qtyInput)) {
      e.preventDefault();
      e.target.blur();
      return;
    }
    if (e.key === 'Escape') {
      const m = $modal();
      if (m && !m.hasAttribute('hidden')) { if (pendingResolve) pendingResolve(false); return; }
      if (isOpen()) { close(); return; }
    }
    if (e.key === 'Tab' && isOpen()) {
      const inner = $inner(); if (!inner) return;
      const focusables = inner.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // ---------------- Add-to-cart auto-open (any source: Dawn, YMQ, custom) ----------------
  // Defensive: clear any stale optimistic empty/hidden state from previous removal flow,
  // so a fresh add-to-cart re-renders cleanly via refetchSections.
  function clearOptimisticState() {
    const inner = $inner();
    if (!inner) return;
    inner.querySelectorAll('.cdc__optimistic-empty, .cdc__empty--optimistic').forEach(el => el.remove());
    inner.querySelectorAll('.cdc__form-wrap, .cdc__form, .cdc__upsell, .cdc__footer, .cdc__shipping-bar').forEach(el => {
      el.style.removeProperty('display');
    });
    const pill = $countPill();
    if (pill) pill.style.removeProperty('display');
    // Force-clear is-empty class (defensive — applySections will set the correct value
    // moments later, but if we're transitioning empty → filled, we want this gone now
    // so the user sees the loader / new items immediately).
    const d = $drawer();
    if (d) d.classList.remove('is-empty');
  }

  // ============================================================
  // Bulletproof post-add hook — invoked by both fetch and XHR interceptors
  // when an EXTERNAL /cart/add.js completes (e.g., PDP add-to-cart).
  // Multiple safety nets:
  //   1) clearOptimisticState() unhides any hidden form/upsell/footer
  //   2) Force-remove is-empty class (in case server section-render is stale)
  //   3) refetchSections() to GET the latest cart-drawer HTML
  //   4) Verify via /cart.js that the cart actually has items; if drawer still
  //      shows is-empty BUT cart has items, retry refetch with longer delay
  //   5) Always open() the drawer, even on error
  // ============================================================
  let postAddInFlight = false;
  async function postAddHook() {
    if (postAddInFlight) return;  // dedup rapid concurrent adds
    postAddInFlight = true;
    try {
      captureBefore();
      clearOptimisticState();

      // ============================================================
      // v7 (2026-09-06): legacy AUTO-ADD GIFT WRAP flow DISABILITATO.
      // Il vecchio product 56061744808260 non è più esposto in UI (rimpiazzato da
      // 2 gift box + PrioPlus+ toggle). Auto-re-add di un product invisibile
      // avrebbe portato a orphan cleanup loop. Un vecchio session cookie
      // 'cdc_gift_preferred=1' viene ora ignorato (e purgato per pulizia).
      // ============================================================
      try {
        if (sessionStorage.getItem(GIFT_PREF_KEY) === '1') {
          sessionStorage.removeItem(GIFT_PREF_KEY);
          console.debug('[post-add/v7] purged legacy gift preference — feature removed');
        }
      } catch (_) {}

      await refetchSections();

      // Verification step: confirm cart and DOM agree
      const cart = await fetchCartSnapshot();
      const drawer = $drawer();
      if (cart && cart.item_count > 0 && drawer && drawer.classList.contains('is-empty')) {
        // Server says cart has items but drawer rendered as empty (stale section response).
        // Retry once with a slightly longer delay to let server-side cache drain.
        console.warn('[cart-drawer] stale empty render detected, retrying refetch', {
          itemCount: cart.item_count, drawerEmpty: true,
        });
        await new Promise(r => setTimeout(r, 250));
        await refetchSections();
      }

      flashAfter();
      bumpCartIcon();
      open();
      emitCartUpdated('add');
    } catch (err) {
      console.error('[cart-drawer] post-add hook failed', err);
      try { open(); } catch (_) {}
    } finally {
      postAddInFlight = false;
    }
  }

  // Helper: read the X-CDC-Internal header from any of the headers shapes fetch accepts
  function isInternalRequest(init) {
    if (!init || !init.headers) return false;
    const h = init.headers;
    if (typeof Headers !== 'undefined' && h instanceof Headers) {
      return h.get(INTERNAL_HEADER) === '1';
    }
    if (Array.isArray(h)) {
      return h.some(p => p && p[0] && p[0].toLowerCase() === INTERNAL_HEADER.toLowerCase() && p[1] === '1');
    }
    if (typeof h === 'object') {
      for (const k in h) {
        if (k.toLowerCase() === INTERNAL_HEADER.toLowerCase() && h[k] === '1') return true;
      }
    }
    return false;
  }

  const _fetch = window.fetch;
  window.fetch = function(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isAdd = /\/cart\/add(\.js)?($|\?)/.test(url);
    const internal = isAdd ? isInternalRequest(init) : false;
    return _fetch.apply(this, arguments).then(async function(res) {
      if (isAdd && res.ok && !internal) {
        setTimeout(postAddHook, 150);
      }
      return res;
    });
  };

  // XHR: track internal flag via setRequestHeader override
  const _xhrSetHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.setRequestHeader = function(name, value) {
    if (name && String(name).toLowerCase() === INTERNAL_HEADER.toLowerCase() && value === '1') {
      this._cdcInternal = true;
    }
    return _xhrSetHeader.apply(this, arguments);
  };

  const _xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function() {
    const xhr = this;
    const orig = xhr.onreadystatechange;
    xhr.onreadystatechange = function() {
      if (orig) orig.apply(xhr, arguments);
      if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
        const u = xhr.responseURL || '';
        if (/\/cart\/add(\.js)?($|\?)/.test(u) && !xhr._cdcInternal) {
          setTimeout(postAddHook, 250);
        }
      }
    };
    return _xhrSend.apply(xhr, arguments);
  };

  document.addEventListener('DOMContentLoaded', function() {
    const d = $drawer(); if (d) d.setAttribute('aria-hidden', 'true');
    setupMobileDrag();
  });

  // ---------------- Outside-click safety net (closes when clicking off-panel) ----------------
  // Use mousedown+click pair to avoid closing on drag-select that STARTS inside the
  // drawer and ENDS outside (legitimate text selection — must not close).
  let outsideMousedownTarget = null;
  document.addEventListener('mousedown', function(e) {
    outsideMousedownTarget = e.target;
  }, true);
  document.addEventListener('click', function(e) {
    const startedInside = outsideMousedownTarget;
    outsideMousedownTarget = null;
    if (!isOpen()) return;
    const inner = $inner();
    if (!inner) return;
    // Click inside drawer panel → ignore
    if (inner.contains(e.target)) return;
    // Click on cart icon → don't auto-close (it opens drawer)
    if (e.target.closest(SEL.cartIcon)) return;
    // If the gesture STARTED inside the drawer (drag-select escape), don't close
    if (startedInside && inner.contains(startedInside)) return;
    close();
  });

  // ---------------- Mobile bottom-sheet: real touch drag-to-close ----------------
  function setupMobileDrag() {
    let startY = 0, currentY = 0, isDragging = false, inner = null;
    const isMobile = () => window.matchMedia('(max-width: 540px)').matches;

    function onStart(e) {
      if (!isMobile() || !isOpen()) return;
      const target = e.target.closest('[data-drag-handle], .cdc__header');
      if (!target) return;
      // Only initiate drag from handle or header (not buttons)
      if (e.target.closest('button, a, input, select')) return;
      startY = e.touches ? e.touches[0].clientY : e.clientY;
      currentY = startY;
      isDragging = true;
      inner = $inner();
      if (inner) inner.style.transition = 'none';
    }

    function onMove(e) {
      if (!isDragging || !inner) return;
      currentY = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = Math.max(0, currentY - startY);
      inner.style.transform = 'translateY(' + delta + 'px)';
      // Damp overlay opacity proportionally
      const overlay = $drawer() && $drawer().querySelector('.cdc__overlay');
      if (overlay) {
        const opacity = Math.max(0, 1 - delta / 400);
        overlay.style.opacity = opacity;
      }
    }

    function onEnd() {
      if (!isDragging || !inner) return;
      isDragging = false;
      const delta = currentY - startY;
      inner.style.transition = '';
      inner.style.transform = '';
      const overlay = $drawer() && $drawer().querySelector('.cdc__overlay');
      if (overlay) overlay.style.opacity = '';
      // Threshold scales with viewport height — 18% of screen, clamped 80–150px
      // (better feel on tiny phones AND tablets vs the old fixed 100px)
      const threshold = Math.min(150, Math.max(80, window.innerHeight * 0.18));
      if (delta > threshold) close();
    }

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
  }

  // ============================================================
  // Recommendations slider — Shopify Recommendations API
  // ============================================================
  const RECS_CACHE_KEY_PREFIX = 'cdc_recs_v6_'; // bumped v5→v6 (blacklist update 2026-06-02) — invalida cache stale che potrebbe contenere prodotti ora blacklisted
  const RECS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  // Blacklist: products that must NEVER appear in recommendations
  // (gift wrap is shown in its own upsell row below, never as a card)
  const RECS_BLACKLIST_IDS = new Set([
    '15391313101124', // yes-wrap-my-order-as-a-gift (upsell, non un prodotto recommendabile)
    '15564108955972', // Lin blacklist 2026-06-02
    '15563278876996', // Lin blacklist 2026-06-02
  ]);
  const RECS_BLACKLIST_HANDLES = new Set(['yes-wrap-my-order-as-a-gift']);

  function getCartProductIdsFromDOM() {
    const items = document.querySelectorAll('.cdc__items > .cdc__item[data-product-id]');
    const ids = new Set();
    items.forEach(li => {
      const id = li.dataset.productId;
      if (id) ids.add(String(id));
    });
    return ids;
  }

  async function getCartProductIds() {
    // Primary: /cart.js (most reliable, no DOM race conditions)
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        const ids = new Set();
        (data.items || []).forEach(item => {
          if (item.product_id) ids.add(String(item.product_id));
        });
        if (ids.size > 0) return ids;
      }
    } catch (e) {}
    // Fallback: DOM
    return getCartProductIdsFromDOM();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function formatRecPrice(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      try {
        const fmt = (window.theme && window.theme.moneyFormat) || (window.Shopify.money_format) || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) {}
    }
    // Fallback: dollars with 2 decimals
    return '$' + (cents / 100).toFixed(2);
  }

  async function fetchRecommendations(productId, limit) {
    // Locale prefix for URL — primary: Shopify.routes.root (most reliable for Shopify Markets sub-folder routing)
    // Fallback: document.documentElement.lang (covers edge cases where Shopify global is not loaded yet)
    let localePrefix = '';
    if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string' && window.Shopify.routes.root !== '/') {
      localePrefix = window.Shopify.routes.root.replace(/\/$/, '');
    } else {
      const locale = (document.documentElement.lang || '').split('-')[0] || '';
      if (locale && locale !== 'en') localePrefix = '/' + locale;
    }

    // Cache key includes locale to prevent cross-locale title leakage
    // (e.g. user visits EN first, then switches to /it-it/ — must not return cached EN titles)
    const localeKey = localePrefix || 'root';
    const cacheKey = RECS_CACHE_KEY_PREFIX + productId + '_' + limit + '_' + localeKey;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.ts && (Date.now() - parsed.ts < RECS_CACHE_TTL_MS)) {
          return parsed.products || [];
        }
      }
    } catch (e) {}

    // Fetch more than needed to allow filtering (cart products + out-of-stock)
    const url = localePrefix + '/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + Math.max(10, limit * 2) + '&intent=related';

    const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Recommendations fetch failed: ' + res.status);
    const data = await res.json();
    const products = Array.isArray(data.products) ? data.products : [];

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products }));
    } catch (e) {}

    return products;
  }

  function renderRecCard(product, btnLabel) {
    const url = product.url || ('/products/' + (product.handle || ''));
    const img = product.featured_image || (product.images && product.images[0]) || '';
    const imgSized = img ? (img.indexOf('?') > -1 ? img + '&width=200' : img.replace(/\.(jpg|jpeg|png|webp)(?=\?|$)/i, '_200x.$1')) : '';
    const title = escapeHtml(product.title || '');

    return (
      '<a class="cdc__rec-card" href="' + url + '" data-rec-product-id="' + product.id + '" aria-label="' + title + '">' +
        '<div class="cdc__rec-card-img-wrap">' +
          (imgSized ? '<img class="cdc__rec-card-img" src="' + imgSized + '" alt="' + title + '" loading="lazy" width="60" height="60">' : '') +
        '</div>' +
        '<div class="cdc__rec-card-name">' + title + '</div>' +
        '<span class="cdc__rec-card-btn">' + escapeHtml(btnLabel) + '</span>' +
      '</a>'
    );
  }

  function bindRecsArrows(container) {
    const slider = container.querySelector('[data-cdc-recs-slider]');
    const prevBtn = container.querySelector('[data-cdc-recs-prev]');
    const nextBtn = container.querySelector('[data-cdc-recs-next]');
    if (!slider || !prevBtn || !nextBtn) return;

    function getStep() {
      const firstCard = slider.querySelector('.cdc__rec-card');
      if (!firstCard) return slider.clientWidth;
      const gap = parseFloat(getComputedStyle(slider).columnGap || getComputedStyle(slider).gap) || 10;
      return firstCard.getBoundingClientRect().width + gap;
    }

    function updateArrows() {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      prevBtn.disabled = slider.scrollLeft <= 4;
      nextBtn.disabled = slider.scrollLeft >= maxScroll - 4;
    }

    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      slider.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      slider.scrollBy({ left: getStep(), behavior: 'smooth' });
    });
    slider.addEventListener('scroll', updateArrows, { passive: true });

    // Initial state after layout
    requestAnimationFrame(updateArrows);
  }

  async function loadRecommendations() {
    const container = document.querySelector('[data-cdc-recs]');
    if (!container) return;
    const slider = container.querySelector('[data-cdc-recs-slider]');
    if (!slider) return;

    const productId = container.dataset.cdcRecsProductId;
    const btnLabel = container.dataset.cdcRecsBtnLabel || 'View product';
    const limit = parseInt(container.dataset.cdcRecsLimit, 10) || 5;
    if (!productId) { container.remove(); return; }

    try {
      // Fetch in parallel: recommendations + cart product IDs (via /cart.js, more reliable than DOM)
      const [all, cartIds] = await Promise.all([
        fetchRecommendations(productId, limit),
        getCartProductIds()
      ]);

      const filtered = all
        .filter(p => p && p.id)
        .filter(p => !cartIds.has(String(p.id))) // exclude products already in cart
        .filter(p => !RECS_BLACKLIST_IDS.has(String(p.id))) // exclude blacklist IDs (e.g. gift wrap)
        .filter(p => !RECS_BLACKLIST_HANDLES.has(String(p.handle || ''))) // exclude blacklist handles
        .filter(p => p.available !== false) // exclude out-of-stock
        .slice(0, limit);

      if (filtered.length === 0) {
        container.remove();
        return;
      }
      slider.innerHTML = filtered.map(p => renderRecCard(p, btnLabel)).join('');
      bindRecsArrows(container);
    } catch (err) {
      console.warn('[CDC recs] failed', err);
      container.remove();
    }
  }

  // Public: re-init recommendations after drawer DOM swap or open
  function initRecommendations() {
    // Defer to next tick so DOM is fully painted
    requestAnimationFrame(() => loadRecommendations());
  }

})();
